import { useMutation, useQuery } from "@apollo/client";
import TableContainer from "@mui/material/TableContainer";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
} from "@mui/material";
import {
  GET_ALL_SPONSORS,
  DELETE_SPONSOR,
  UPDATE_SPONSOR,
} from "./graphql";
import { LinkButton } from "../../components/LinkButton";
import { PageContainer } from "../../components/PageContainer";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import { UpdateMiscConfigButton } from "../links/updateMiscConfig";
import React from "react";

type SponsorRow = {
  id: string;
  name: string;
  order: number;
  value: any;
};

const normalizeOrder = (value?: number | null): number => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return 0;
  }

  return Math.max(0, value);
};

const reorderSponsors = (
  items: SponsorRow[],
  draggedSponsorId: string,
  targetSponsorId: string
): SponsorRow[] => {
  const fromIndex = items.findIndex((item) => item.id === draggedSponsorId);
  const toIndex = items.findIndex((item) => item.id === targetSponsorId);
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
    return items;
  }

  const reordered = [...items];
  const [draggedItem] = reordered.splice(fromIndex, 1);
  reordered.splice(toIndex, 0, draggedItem);

  return reordered.map((item, index) => ({
    ...item,
    order: index,
  }));
};

const Sponsors = () => {
  const { data, loading, refetch } = useQuery(GET_ALL_SPONSORS, {
    fetchPolicy: "cache-first",
  });
  const [updateSponsor] = useMutation(UPDATE_SPONSOR);
  const [rows, setRows] = React.useState<SponsorRow[]>([]);
  const [draggingSponsorId, setDraggingSponsorId] = React.useState<
    string | null
  >(null);
  const [dropTargetSponsorId, setDropTargetSponsorId] = React.useState<
    string | null
  >(null);
  const [isSavingOrder, setIsSavingOrder] = React.useState(false);
  const [deleteSponsor] = useMutation(DELETE_SPONSOR, {
    onCompleted: () => {
      toast("Sponsor deleted");
      refetch();
    },
  });

  React.useEffect(() => {
    const nextRows =
      data?.sponsors.map((sponsor) => ({
        id: sponsor.id,
        name: sponsor.name,
        order: normalizeOrder(sponsor.order),
        value: sponsor.value,
      })) ?? [];

    setRows(nextRows);
  }, [data]);

  const originalOrderById = React.useMemo(() => {
    const map = new Map<string, number>();
    (data?.sponsors ?? []).forEach((sponsor) => {
      map.set(sponsor.id, normalizeOrder(sponsor.order));
    });
    return map;
  }, [data]);

  const hasOrderChanges = React.useMemo(() => {
    return rows.some((row) => originalOrderById.get(row.id) !== row.order);
  }, [originalOrderById, rows]);

  const handleDelete = (sponsorId: string) => {
    confirmAlert({
      title: "Delete this Sponsor?",
      message: `Are you sure? this can't be undone.`,
      buttons: [
        {
          label: "Yes",
          onClick: () => deleteSponsor({ variables: { sponsorId: sponsorId } }),
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  const handleSaveOrder = async () => {
    if (!hasOrderChanges || isSavingOrder) return;

    setIsSavingOrder(true);
    let updatedCount = 0;
    let firstError: string | null = null;

    for (const row of rows) {
      const previousOrder = originalOrderById.get(row.id);
      if (previousOrder === row.order) continue;

      try {
        await updateSponsor({
          variables: {
            id: row.id,
            name: row.name,
            order: normalizeOrder(row.order),
            value: row.value,
          },
        });
        updatedCount += 1;
      } catch (err) {
        firstError =
          firstError ??
          (err instanceof Error ? err.message : "Failed to update sponsor");
      }
    }

    if (updatedCount > 0) {
      toast(
        updatedCount === 1
          ? "Sponsor order updated"
          : `${updatedCount} sponsor orders updated`
      );
      await refetch();
    }

    if (firstError) {
      toast.error(`Some sponsor orders could not be saved: ${firstError}`);
    }

    setIsSavingOrder(false);
  };

  if (loading) return null;

  return (
    <PageContainer title="Sponsors">
      <div style={{ display: "flex", flexDirection: "row", gap: 8 }}>
        <LinkButton
          path="/sponsor/create"
          text="Create new sponsor"
          variant={"contained"}
        />
        <Button
          variant="contained"
          onClick={handleSaveOrder}
          disabled={!hasOrderChanges || isSavingOrder}
        >
          {isSavingOrder ? "Saving..." : "Save order"}
        </Button>
        <UpdateMiscConfigButton />
      </div>
      <Box sx={{ mt: 1.5, color: "text.secondary", fontSize: 14 }}>
        Drag rows to reorder sponsors, then save changes.
      </Box>
      <TableContainer component={Paper} style={{ marginTop: 16 }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell align="left" sx={{ width: 80 }}>
                drag
              </TableCell>
              <TableCell align="left">name</TableCell>
              <TableCell align="left">order</TableCell>
              <TableCell align="center">actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.id}
                draggable
                onDragStart={() => {
                  setDraggingSponsorId(row.id);
                }}
                onDragEnd={() => {
                  setDraggingSponsorId(null);
                  setDropTargetSponsorId(null);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  if (dropTargetSponsorId !== row.id) {
                    setDropTargetSponsorId(row.id);
                  }
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  if (!draggingSponsorId) return;
                  setRows((current) =>
                    reorderSponsors(current, draggingSponsorId, row.id)
                  );
                  setDraggingSponsorId(null);
                  setDropTargetSponsorId(null);
                }}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                style={{
                  opacity: draggingSponsorId === row.id ? 0.55 : 1,
                  backgroundColor:
                    dropTargetSponsorId === row.id
                      ? "rgba(25, 118, 210, 0.08)"
                      : undefined,
                  cursor: "move",
                }}
              >
                <TableCell align="left">::</TableCell>
                <TableCell align="left">{row.name}</TableCell>
                <TableCell align="left">{row.order}</TableCell>
                <TableCell align="center">
                  <LinkButton path={`/sponsor/${row.id}`} text="View" />
                  <Button
                    title="Delete"
                    variant="contained"
                    color="error"
                    onClick={() => handleDelete(row.id)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </PageContainer>
  );
};

export default Sponsors;
